import React, { lazy, MutableRefObject, useCallback, useEffect, useMemo, useState } from "react";
import { AeronetV6ItemsResponse, AeronetViewProps } from "../types";
import AeronetDataGrid, { QueryResults } from "../components/datagrid/DataGrid";
import {
  GridApi,
  GridColDef,
  GridFilterModel,
  GridSortItem,
  GridValueFormatterParams,
  ValueOptions,
} from "@mui/x-data-grid-pro";
import { AxiosResponse } from "axios";
import { InvoiceDeletePayload, InvoiceLines, InvoiceList } from "./types";
import { axios } from "../api";
import queryString from "query-string";
import { useSnackbar } from "notistack";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useNavigate } from "react-router-dom";
import { Button, IconButton ,Link as MaterialLink} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { isV2StationeryPreviewEnabled } from "../utils/stationery";
import { parseISO, format } from "date-fns";
import AlertDialog from "../components/AlertDialog";
import { ActionMenuItem } from "../components/datagrid/ActionMenu";
import { EXPORT_TYPE, useCreateExportBatchAction } from "../batchexports/CreateExportBatchAction";
import { invoiceExportEligibility } from "../batchexports/eligibility";
import PdfInlinePreviewDialog from "../components/pdf/PdfInlinePreviewDialog";
import LoadingSpinner from "../components/Loading";
import InvoicePreview from "./InvoicePreview";
import { buildInvoicePdfViaStationeryV2 } from "./InvoicePdfV2Builder";
import { IDivision } from "../jobs/types";
import InvoiceStatusUpdate from "./InvoiceStatusUpdate";
import XeroConnectRequiredDialog from "../xero/XeroConnectRequiredDialog";
import XeroPushResult from "../xero/XeroPushResult";
import { useXeroModuleInstalled } from "../xero/hooks/useXeroModuleInstalled";
import { useXeroPush } from "../xero/hooks/useXeroPush";
const InvoiceAdvancedSearchForm = lazy(() => import("./AdvancedSearchForm"));

interface InvoiceDataGridProps extends AeronetViewProps {
  invoiceConfig: any;
  canCreateInvoice: boolean;
  canDeleteInvoice: boolean;
}

const InvoiceDataGrid: React.FC<InvoiceDataGridProps> = (props) => {
  const { invoiceConfig, canCreateInvoice, canDeleteInvoice } = props
  const { enqueueSnackbar } = useSnackbar()
  const xeroInstalled = useXeroModuleInstalled();
  const {
    push: xeroPush,
    loading: xeroPushLoading,
    outcome: xeroPushResult,
    clearOutcome: clearXeroPushResult,
    connectRequired: xeroConnectRequired,
    closeConnectRequired: closeXeroConnectRequired,
    wrongOrg: xeroWrongOrg,
    email: xeroEmail,
  } = useXeroPush(xeroInstalled === true);
  // const [invoice,SetInvoice] = useState<InvoiceList[]>([])
  const currentUser = useCurrentUser();
  const globalConfig = useGlobalConfig();
  const useStationeryV2InlinePreview = isV2StationeryPreviewEnabled(globalConfig);
  const navigate = useNavigate();
  const [tabDialogOpen, setTabDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<InvoiceList[]>([]);
  const [rows, setRows] = useState<InvoiceList[]>([]);
  const [onchangeRow, setOnchangeRow] = useState<InvoiceList[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = React.useState<any[]>([]);
  const [gridKey, setGridKey] = useState(0);
  const [deletePayload, setDeletePayload] =
    useState<InvoiceDeletePayload | null>(null);
  const [previewSelected, setPreviewSelected] = React.useState<number>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [inlinePreviewOpen, setInlinePreviewOpen] = useState(false);
  const [inlinePreviewBlob, setInlinePreviewBlob] = useState<Blob | null>(null);
  const [inlinePreviewFilename, setInlinePreviewFilename] = useState("");
  // The push outcome carries ids only, so the names the drawer shows come from
  // the rows that were selected when the push started. They outlive the
  // selection because a Retry failed from the drawer pushes the same records.
  const [xeroPushLabels, setXeroPushLabels] = useState<Record<number, string>>(
    {}
  );
    const [divisions,setDivisions] = useState<IDivision>()
    const [divisionsOptions,setDivisionsOptions] = useState<ValueOptions[]>([])
  useEffect(()=>{
    axios
    .get("/v6/jobs/divisions")
    .then((resp) => {
      let divisionList: ValueOptions[] = [];
      let divisionDict = {};
      divisionList = resp?.data?.data?.map((division:IDivision) => {
        // return 
        divisionDict[division.id] = division.name
        return {
          value: division.id,
          label: division.name,
        }
      })
      setDivisions(divisionDict as IDivision)
      setDivisionsOptions(divisionList);
      return null;
    })
    .catch((error) => {
      enqueueSnackbar(`Error fetching divisions `, {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    });
  },[enqueueSnackbar])  
  const statusOptions: ValueOptions[] | undefined = useMemo(() => {
    if (invoiceConfig && invoiceConfig.status) {
      const statuses: ValueOptions[] = [{ value: "", label: "Any" }];
      Object.keys(invoiceConfig?.status).forEach((key) => {
        statuses.push({
          value: key,
          label: invoiceConfig?.status[key],
        });
      });
      return statuses.sort((a, b) =>
        // @ts-ignore
        a.label!.localeCompare(b.label!, "en", {
          sensitivity: "base",
        })
      );
    }
  }, [invoiceConfig]);
  const columns: GridColDef[] = useMemo(() => [
    {
      headerName: "ID",
      field: "id",
      flex: 1,
      filterable: false
    },
    {
      headerName: "Invoice To",
      field: "client_name",
      flex: 1,
      filterable: false
    },
    {
      headerName: "Date Created",
      field: "date_created",
      flex: 1,
      filterable: false,
      valueGetter: ({ value }) => value,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value && globalConfig) {
          return format(parseISO(params.value), globalConfig.date_format);
        }
        return params.value;
      },
    },
    {
      headerName: "Status",
      field: "status_id",
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (Number(params.value) === 0) {
          return "";
        }
        try {
          if (invoiceConfig) {
            return invoiceConfig.status[
              Number(params.value as number)
            ].toUpperCase();
          }
        } catch {
          return params.value;
        }
      },
        type: "singleSelect",
        valueGetter: (params) => params.row?.status_id,
        filterable: true,
        valueOptions: statusOptions,
      },
    {
      headerName: "Work Order",
      field: "job_id",
      flex: 1,
      filterable: false,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value === 0 || params.value === "0" || params.value == null) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Order Number",
      field: "purchase_order",
      flex: 1,
      filterable: false
    },
    {
      headerName: "Division",
      field: "division",
      flex: 1,
      valueFormatter: (
        params: GridValueFormatterParams & { row?: InvoiceList }
      ) => {
        if (Number(params.value) === 0) {
          return "";
        }
        try {
          if (divisionsOptions.length > 0 && divisions) {
            return divisions[Number(params.value as number)];
          }
          return params.row?.division_obj?.name ?? params.value;
        } catch {
          return params.value;
        }
      },
      filterable: true,
      valueOptions: divisionsOptions,
      type: "singleSelect",
    },
    {
      headerName: "Created By",
      field: "created_by_user.name",
      // Server-side sort would 422 the whole list: `created_by_user.name` resolves through
      // `InvoiceList.created_by_user (no alias)` to a sqlalchemy model
      // named `CreatedByUser`, which does not exist, so sqlalchemy_filters raises
      // FieldNotFound -> RequestValidationError. A 422 empties the grid, so every
      // row vanishes after a header click. Measured on contacts 2026-08-22.
      sortable: false,
      flex: 1,
      valueGetter: (params) => params.row?.created_by_user?.name,
      filterable: false
    },
    {
      headerName: "Reference",
      field: "reference",
      flex: 1,
      filterable: false
    },
    {
      headerName: "Total",
      field: "total",
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) => {
        
        return params && Number(params?.value).toFixed( globalConfig?.value_rounding ?? 4)
      },
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => params.row?.total,
      filterable: false
    },
    {
      headerName: "Currency",
      field: "currency_code",
      flex: 0.5,
      filterable: false
    },
    {
      headerName: "PDF",
      field: "pdf",
      flex: 0.5,
      filterable: false,
      renderCell: (params) => {
        return (
          <IconButton
              className="pdf-icon-button"
              component={MaterialLink}
              rel="noopener noreferrer"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (useStationeryV2InlinePreview) {
                  setPreviewLoading(true);
                  void (async () => {
                    try {
                      const { blob, filename } =
                        await buildInvoicePdfViaStationeryV2({
                          invoiceId: params.row.id,
                        });
                      setInlinePreviewBlob(blob);
                      setInlinePreviewFilename(filename);
                      setInlinePreviewOpen(true);
                    } catch {
                      enqueueSnackbar(
                        `Failed to render invoice ${params.row.id}`,
                        { variant: "error" }
                      );
                    } finally {
                      setPreviewLoading(false);
                    }
                  })();
                  return;
                }

                setPreviewLoading(true);
                setPreviewSelected(params.row.id);
              }}
            >
              <PictureAsPdfIcon />
            </IconButton>
        );
      },
    }
  ], [
    invoiceConfig,
    globalConfig,
    useStationeryV2InlinePreview,
    statusOptions,
    divisions,
    divisionsOptions,
    enqueueSnackbar,
  ])




  const getData = useCallback(
    async (params: any, axiosConfig: any) => {
      try {
        const resp: AxiosResponse<AeronetV6ItemsResponse<InvoiceList>> =
          await axios.get("/v6/invoice", {
            params: params,
            paramsSerializer: (params) => queryString.stringify(params),
            ...axiosConfig,
          });
        setRows(Array.from(resp.data.items));

        return {
          total: resp.data.total,
          rows: resp.data.items,
        } as QueryResults<InvoiceList>;
      } catch (e) {
        // @ts-ignore
        if (!axios.isCancel(e)) {
          enqueueSnackbar("Error fetching invoices", {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        }
        throw e;
      }
    }, [enqueueSnackbar])

  const quickSearchItems = useMemo(
    () => [
      {
        field: "id",
        operator: "startsWith",
        label: "ID",
      },
      {
        field: "reference",
        operator: "contains",
        label: "Reference",
      },
      {
        field: "purchase_order",
        operator: "contains",
        label: "Order Number",
      },
      {
        field: "client_name",
        operator: "contains",
        label: "Invoice To",
      },
      {
        field: "part_number",
        operator: "contains",
        label: "Part Number",
      },
      {
        field: "job_id",
        operator: "contains",
        label: "Work Order Number",
      },
      {
        field: "created_by",
        operator: "contains",
        label: "Created By",
      },
    ],
    []
  );

  const advancedSearchDefaultItems = useMemo(
    () => [
      {
        id: 1,
        field: "id",
        operator: "is",
        value: "",
      },
      {
        id: 2,
        field: "purchase_order",
        operator: "contains",
        value: "",
      },
      {
        id: 3,
        field: "reference",
        operator: "contains",
        value: "",
      },
      {
        id: 4,
        field: "status_id",
        operator: "contains",
        value: "",
      },
      {
        id: 5,
        field: "division",
        operator: "contains",
        value: "",
      },
      {
        id: 6,
        field: "client_name",
        operator: "contains",
        value: "",
      },
      {
        id: 7,
        field: "created_by",
        operator: "contains",
        value: "",
      },
      {
        id: 8,
        field: "job_id",
        operator: "contains",
        value: "",
      },
    ],
    []
  );
  console.log(canDeleteInvoice, "can delete invoice")

  const onPushToXeroClick = useCallback(
    async (event: InvoiceDeletePayload) => {
      const invoices = ([...event.selectedRows] as InvoiceList[]).filter(
        (row) => row.id != null
      );
      const ids = invoices.map(({ id }) => id);
      // An invoice with no client would render as a bare "#12" drawer row
      // anyway, so leave it out and let the drawer fall back to its id.
      setXeroPushLabels(
        Object.fromEntries(
          invoices
            .filter(({ client_name }) => client_name?.trim())
            .map(({ id, client_name }) => [id, `#${id} ${client_name}`])
        )
      );
      const pushed = await xeroPush("invoices", ids);
      if (pushed) {
        // A failed chunk stops the push, so the ids behind it never went up.
        // Deselect only what the service answered for and leave the rest
        // selected, or the user has to find those rows again to retry them.
        const attempted = new Set(pushed.results.map((row) => row.id));
        const remaining = ids.filter((id) => !attempted.has(id));
        // The grid's selection is controlled from here, so the row state is
        // what actually redraws the checkboxes; the payload setter keeps the
        // grid's own model in step with it.
        event.setSelectionModel(remaining);
        setRowSelectionModel(remaining);
        setOnchangeRow(invoices.filter(({ id }) => !attempted.has(id)));
      }
    },
    [xeroPush]
  );

  /**
   * Retry from the drawer goes back through the hook with the failed ids only.
   * `xeroPushLabels` still holds their names, so the drawer keeps naming them.
   */
  const onRetryXeroPush = useCallback(
    (ids: number[]) => {
      xeroPush("invoices", ids);
    },
    [xeroPush]
  );

  // H255: tick records here, ACTIONS → Create Export Batch (Accounts Exports).
  // H281: greyed, with the reason, when no ticked invoice can export.
  const exportEligibility = useMemo(() => invoiceExportEligibility(invoiceConfig), [invoiceConfig]);
  const createExportBatch = useCreateExportBatchAction(EXPORT_TYPE.INVOICES, "invoice", exportEligibility);

  const actions: ActionMenuItem[] = useMemo(() => {
    const defaultActions: ActionMenuItem[] = [
      {
        name: "Set Status",
        action: ["SetStatus", "SET_STATUS"],
        requiresSelection: true,
        multipleSelection: true,
      },
    ];
    if (xeroInstalled === true) {
      defaultActions.push({
        name: "Push to Xero",
        onClick: onPushToXeroClick,
        requiresSelection: true,
        multipleSelection: true,
        isReturnDataSelection: true,
      });
    }
    if (canDeleteInvoice) {
      defaultActions.push({
        name: "Delete",
        namePlural: "Delete",
        action: ["Delete", "DELETE"],
        requiresSelection: true,
        multipleSelection: true,
        onClick: async (event: InvoiceDeletePayload) => {
          const rows = [...event.selectedRows] as InvoiceList[];
          for (let index = 0; index < rows.length; index++) {
            const row = rows[index];
            let lines: InvoiceLines[] = [];

            await axios
              .get(`/v6/invoice/lines/${row.id}`, {
                id: `get-lines-${row.id}`,
              })
              .then(
                (
                  resp: AxiosResponse<InvoiceLines>
                ) => {
                  axios.storage.remove(`get-lines-${row.id}`)
                  //@ts-ignore
                  lines = resp.data;
                }
              );
            console.log(lines, "lines")
            if (lines.length > 0) {
              enqueueSnackbar("Selected row has linked records", {
                variant: "warning",
              });
              return;
            }
          }
          setTabDialogOpen(true);
          setDeletePayload(event);
          setSelectedRows(rows);
        },
      });
    }
    if (createExportBatch) {
      defaultActions.push(createExportBatch);
    }

    return defaultActions;
  }, [canDeleteInvoice, enqueueSnackbar, xeroInstalled, onPushToXeroClick, createExportBatch]);


  const deleteInvoices = useCallback(() => {
    const invoiceIDs = selectedRows.map((row) => row.id);
    if (!invoiceIDs || invoiceIDs.length === 0) {
      return;
    }
    for (const id of invoiceIDs) {
      axios
        .delete(`/v6/invoice/${id}`)
        .then(() => {
          enqueueSnackbar(`Deleted invoices ${id}`, {
            variant: "success",
          });
          if (deletePayload?.apiRef) {
            deletePayload.apiRef.current.updateRows([
              { id: id, _action: "delete" },
            ]);
          }
        })
        .catch((_error) => {
          enqueueSnackbar("Error deleting invoices", {
            variant: "error",
          });
        });
    }
    deletePayload?.setSelectionModel([]);
    setDeletePayload(null);
    setSelectedRows([]);
  }, [deletePayload, enqueueSnackbar, selectedRows]);


  const onRowClick = useCallback(
    (params, event) => {
      event.stopPropagation();

      let currentElement = event.target;

      // Traverse up the DOM tree to check if any parent element has the 'pdf-icon-button' class
      while (currentElement) {
        // Ensure className is a string before calling includes
        if (
          typeof currentElement.className === "string" &&
          currentElement.className.includes("pdf-icon-button")
        ) {
          // If it's part of the PDF icon button, do not navigate
          return;
        }
        currentElement = currentElement.parentElement;
      }

      // If the clicked element is not part of the PDF icon, navigate
      navigate(String(params.id));
    },
    [navigate]
  );

  const onSearch = useCallback(
    (filterModel: GridFilterModel, apiRef: MutableRefObject<GridApi>) => {
      console.log("FilterModel....", filterModel);
      apiRef.current.setFilterModel(filterModel, "upsertFilterItems");
    },
    []
  );

  const initialSort: GridSortItem[] = useMemo(
    () => [{ field: "id", sort: "desc" }],
    []
  );


  const handleClose = useCallback(() => {
    setPreviewSelected(undefined);
    setPreviewLoading(false);
  }, []);

  const handleInlinePreviewClose = useCallback(() => {
    setInlinePreviewOpen(false);
    setInlinePreviewBlob(null);
    setInlinePreviewFilename("");
  }, []);


  return (
    <>
      <AeronetDataGrid
        key={gridKey}
        advancedSearchProps={{
          items: advancedSearchDefaultItems,
          onSearch: onSearch,
        }}
        quickSearchItems={quickSearchItems}
        columns={columns}
        data={getData}
        initialSort={initialSort}
        currentUserId={currentUser?.id}
        enableCreate={canCreateInvoice}
        name="invoice-data-grid"
        initialState={{
          columns: {
            columnVisibilityModel: false,
          },
        }}
        onRowClick={onRowClick}
        advancedSearchForm={InvoiceAdvancedSearchForm}
        disableColumnFilter={false}
        enableFilter={true}
        actions={actions}
        onRowSelectionModelChange={(newRowSelectionModel: any) => {
          setRowSelectionModel(newRowSelectionModel);
          const selectedRows = rows.filter((row) =>
            newRowSelectionModel.includes(row.id)
          );
          setOnchangeRow(selectedRows);
        }}
        rowSelectionModel={rowSelectionModel}
      >
        {(props) => {
          return (
            <>
              {onchangeRow &&
              onchangeRow.length >= 1 &&
              props.Actions.SetStatus ? (
                <InvoiceStatusUpdate
                  open={props.action === props.Actions.SetStatus}
                  invoices={onchangeRow}
                  config={invoiceConfig}
                  onUpdated={() => {
                    setSelectedRows([]);
                    setRowSelectionModel([]);
                    setOnchangeRow([]);
                    setGridKey((k) => k + 1);
                  }}
                  {...props}
                />
              ) : (
                <></>
              )}
            </>
          );
        }}
      </AeronetDataGrid>
      {previewSelected ? (
        <InvoicePreview
          invoiceId={previewSelected}
          onClose={handleClose}
          onReady={() => setPreviewLoading(false)}
        />
      ) : null}
      <LoadingSpinner open={previewLoading || xeroPushLoading} />
      <PdfInlinePreviewDialog
        open={inlinePreviewOpen}
        blob={inlinePreviewBlob}
        filename={inlinePreviewFilename}
        title="Invoice Preview"
        onClose={handleInlinePreviewClose}
      />
      <AlertDialog
        title="Delete this record?"
        open={tabDialogOpen}
        handleClose={() => {
          setTabDialogOpen(false);
        }}
      >
        <Button
          onClick={() => {
            setTabDialogOpen(false);
          }}
          color="primary"
        >
          No
        </Button>
        <Button
          onClick={() => {
            setTabDialogOpen(false);
            deleteInvoices();
          }}
          color="primary"
          autoFocus
        >
          Yes
        </Button>
      </AlertDialog>
      <XeroConnectRequiredDialog
        open={xeroConnectRequired}
        onClose={closeXeroConnectRequired}
        wrongOrg={xeroWrongOrg}
      />
      <XeroPushResult
        outcome={xeroPushResult}
        labels={xeroPushLabels}
        pushedBy={xeroEmail ?? undefined}
        loading={xeroPushLoading}
        onClose={clearXeroPushResult}
        onRetryFailed={onRetryXeroPush}
      />
    </>
  );
};


export default InvoiceDataGrid;
